import { createAction } from '@reduxjs/toolkit'
import { FileFolder, MyFile } from 'interfaces'
import { firstValueFrom, Subscriber } from 'rxjs'
import { sortAscending } from 'shared/utils'
import StoreState, { createAppAsyncThunk } from 'store/state'
import toCamelCase from 'utils/toCamelCase'
import { myLibraryFoldersAdapter, storageInfoAdapter } from './adapters'
import { api } from './net'
import { myFileFoldersArraySelector, myFileFoldersSelector } from './selectors'
import { getFileThumbUrl } from './util'

export const setFileInPreview = createAction<MyFile | undefined>('uploads/setFileInPreview')
export const setUploaderFile = createAction<File | undefined>('uploads/setUploaderFile')
export const setUploadsDialogOpen = createAction<boolean>('uploads/setUploadsDialogOpen')
export const setUploaderFiles = createAction<{ [name: string]: File }>('uploads/setUploaderFiles')
export const setEditFolder = createAction<FileFolder | undefined>('uploads/setEditFolder')
export const setSelectedFile = createAction<MyFile | undefined>('uploads/setSelectedFile')

export const getUploads = createAppAsyncThunk(
  'uploads/getUploads',
  async (force: boolean = true, { dispatch, getState }) => {
    if (!force) {
      const storedFolders = myFileFoldersSelector(getState() as StoreState)
      if (Object.keys(storedFolders).length !== 0) {
        return { folders: storedFolders }
      }
    }
    const response = await firstValueFrom(dispatch(api.getUploads()))
    return { folders: myLibraryFoldersAdapter(response.usersUploadsData) }
  }
)

export const getStorageInfo = createAppAsyncThunk(
  'uploads/getStorageInfo',
  async (_, { dispatch }) => {
    const response = await firstValueFrom(dispatch(api.getStorageInfo()))
    return { storage: storageInfoAdapter(response) }
  }
)

export const deleteFile = createAppAsyncThunk(
  'uploads/deleteFile',
  async (args: { file: MyFile, force?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(api.deleteFiles([args.file], args.force)))
      return { file: args.file }
    } catch (error) {
      return rejectWithValue({ ...error, file: args.file })
    }
  }
)

export const deleteFiles = createAppAsyncThunk(
  'uploads/deleteFiles',
  async (args: { files: MyFile[], force?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(api.deleteFiles(args.files, args.force)))
      return { files: args.files }
    } catch (error) {
      const res = toCamelCase(error.response) as any
      return rejectWithValue({ ...res, files: args.files })
    }
  }
)

export const deleteFolder = createAppAsyncThunk(
  'uploads/deleteFolder',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(api.deleteFolder(id)))
      return { id }
    } catch (error) {
      return rejectWithValue({ id, ...error })
    }
  }
)

export const reorderFolders = createAppAsyncThunk(
  'uploads/reorder',
  async (ids: string[], { dispatch }) => {
    await firstValueFrom(dispatch(api.reorderFolders(ids)))
    return { ids }
  }
)

export const updateFolder = createAppAsyncThunk(
  'uploads/updateFolder',
  async (
    args: { id: string, name: string, color: string, isPrivate: boolean, featuredImage?: string | File, coverImages?: string[] },
    { dispatch, rejectWithValue }) => {
    const { id, name, color, isPrivate, featuredImage, coverImages } = args
    try {
      const response = await firstValueFrom(dispatch(api.updateFolder(id, name, color, isPrivate, featuredImage, coverImages)))
      const cover = JSON.parse(response.images)
      // eslint-disable-next-line no-magic-numbers
      const data: any = { id, name, color, isPrivate, coverImages: coverImages || cover.slice(1, 3) }
      if (typeof featuredImage === 'string') {
        data.featuredImage = featuredImage
      } else if (cover[0]) {
        data.featuredImage = cover[0]
      }
      return data
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

type UploadFileArgs = {
  file: File,
  folder: Partial<FileFolder>,
  width: number,
  height: number,
  rotateByDegrees?: number,
  progressSubscriber?: Subscriber<any>,
  uploadId?: string,
  compress?: boolean
}

export const uploadFile = createAppAsyncThunk(
  'uploads/uploadFile',
  async (args: UploadFileArgs, { dispatch, rejectWithValue }) => {
    const { file, folder, width, height, rotateByDegrees, progressSubscriber, uploadId, compress } = args
    try {
      const response = await firstValueFrom(dispatch(api.uploadFile(
        file, width, height, folder.id, folder.name, folder.color, folder.isPrivate, rotateByDegrees, progressSubscriber, compress)
      ))

      const folderId = folder.id || response.folderId
      // Annotated so the payload carries MyFile rather than the shape inferred here - without it
      // `createdAt` leaks toISOString's template-literal type and no plain MyFile is assignable.
      const uploadedFile: MyFile = {
        id: response.fileId,
        key: `${response.fileId}-${response.fileType}`,
        folderId,
        size: file.size,
        width,
        height,
        name: response.fileName,
        url: response.fileLink,
        thumbUrl: getFileThumbUrl(response.fileLink, response.fileType),
        type: response.fileType,
        createdAt: new Date().toISOString()
      }
      return {
        folderId,
        folderName: folder.name,
        folderColor: folder.color,
        folderPrivate: folder.isPrivate,
        uploadId,
        file: uploadedFile
      }
    } catch (error) {
      return rejectWithValue({ fileName: file.name, source: error, uploadId })
    }
  }
)

export const createFolder = createAppAsyncThunk(
  'uploads/createFolder',
  async (
    args: { name: string, color: string, isPrivate: boolean, featuredImage?: File, coverImages?: File[] },
    { dispatch, getState, rejectWithValue }
  ) => {
    const { name, color, isPrivate, featuredImage, coverImages } = args
    try {
      const response = await firstValueFrom(dispatch(api.createFolder(name, color, isPrivate, featuredImage, coverImages)))
      const id = response.id.toString()
      const folders = myFileFoldersArraySelector(getState() as StoreState)
      const order = folders.sort(sortAscending('order')).map(f => f.id)
      order.push(id)
      dispatch(reorderFolders(order))
      return {
        folder: {
          id,
          name,
          color,
          isPrivate,
          filesCount: 0,
          featuredImage: response.featured,
          files: {},
          filters: []
        }
      }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const copyFileToFolder = createAppAsyncThunk(
  'uploads/copyFileToFolder',
  async (args: { file: MyFile, folder: { id: string, name: string } }, { dispatch, rejectWithValue }) => {
    const { file, folder } = args
    try {
      const response = await firstValueFrom(dispatch(api.copyFileToFolder(file, folder)))
      return {
        file: {
          ...file,
          id: response.id,
          key: `${response.id}-${file.type}`,
          url: response.link,
          thumbUrl: getFileThumbUrl(response.link, file.type),
          folderId: folder.id
        },
        folder
      }
    } catch (error) {
      return rejectWithValue({ file, folder, ...error })
    }
  }
)
